// ============================================================================
// FNE-BRIDGE Frontend - Configuration des routes
// ============================================================================

// --- Routes publiques (pas d'authentification requise) ---
export const PUBLIC_ROUTES = {
  LOGIN: '/login',
  RESET_PASSWORD: '/reset-password',
  RESET_PASSWORD_CONFIRM: '/reset-password/confirm',
  CREATE_ACCOUNT: '/create-account',
} as const;

// --- Routes protégées (authentification requise) ---
export const PROTECTED_ROUTES = {
  // Dashboard
  DASHBOARD: '/',

  // Stickers
  STICKERS: '/stickers',

  // Factures / Reçus
  INVOICES: '/invoices',
  INVOICE_NEW: '/invoices/new',
  INVOICE_DETAIL: '/invoices/:id',
  INVOICE_EDIT: '/invoices/:id/edit',
  INVOICE_REFUND: '/invoices/:id/refund',
  INVOICE_IMPORT: '/invoices/import',
  INVOICES_RECEIVED: '/invoices-received',
  INVOICES_RECEIVED_DETAIL: '/invoices-received/:id',

  // Clients / Fournisseurs
  CLIENTS: '/clients',
  CLIENT_STATISTICS: '/clients/statistics',
  CLIENT_NEW: '/clients/new',
  CLIENT_IMPORT: '/clients/import',
  CLIENT_DETAIL: '/clients/:id',
  CLIENT_EDIT: '/clients/:id/edit',
  SUPPLIERS: '/suppliers',
  SUPPLIER_STATISTICS: '/suppliers/statistics',
  SUPPLIER_NEW: '/suppliers/new',
  SUPPLIER_IMPORT: '/suppliers/import',
  SUPPLIER_DETAIL: '/suppliers/:id',
  SUPPLIER_EDIT: '/suppliers/:id/edit',

  // Paramétrage
  SETTINGS: '/settings',

  // Gestion utilisateurs (admin)
  USERS: '/users',
  USER_NEW: '/users/new',

  // Articles (nomenclature / produits)
  NOMENCLATURE: '/articles',
  NOMENCLATURE_NEW: '/articles/new',
  NOMENCLATURE_IMPORT: '/articles/import',
  NOMENCLATURE_DETAIL: '/articles/:id',
  NOMENCLATURE_EDIT: '/articles/:id/edit',

  // Connexions BDD
  CONNECTIONS: '/connections',
  CONNECTION_NEW: '/connections/new',
  CONNECTION_DETAIL: '/connections/:id',

  // Mappings
  MAPPINGS: '/mappings',
  MAPPING_DETAIL: '/mappings/:id',

  // Synchronisation
  SYNC: '/sync',

  // Aide
  HELP: '/help',

  // Profil
  PROFILE: '/profile',
  CHANGE_PASSWORD: '/change-password',

  // Notifications
  NOTIFICATIONS: '/notifications',
} as const;

// --- Navigation principale (sidebar) ---
export interface NavItem {
  label: string;
  path: string;
  icon: string;
  roles?: string[];
  /** Permission section key (from permissions.ts) required to see this nav item */
  permSection?: string;
  /** Permission action key (e.g., 'clients', 'fournisseurs') for fine-grained control */
  permAction?: string;
  children?: NavItem[];
  badge?: string;
  dividerAfter?: boolean;
}

export const MAIN_NAV: NavItem[] = [
  // --- Bloc principal ---
  { label: 'Tableau de bord', path: PROTECTED_ROUTES.DASHBOARD, icon: 'Dashboard', permSection: 'dashboard' },
  { label: 'Gestion stickers', path: PROTECTED_ROUTES.STICKERS, icon: 'ConfirmationNumber', permSection: 'stickers' },
  { label: 'Factures / Reçus', path: PROTECTED_ROUTES.INVOICES, icon: 'Receipt', permSection: 'factures', children: [
    { label: 'Reçus et factures émis', path: PROTECTED_ROUTES.INVOICES, icon: 'List', permSection: 'factures' },
    { label: 'Reçus et factures réceptionnés', path: PROTECTED_ROUTES.INVOICES_RECEIVED, icon: 'CallReceived', permSection: 'factures_receptionnees' },
  ]},
  { label: 'Clients et Fournisseurs', path: PROTECTED_ROUTES.CLIENTS, icon: 'People', permSection: 'entreprises', children: [
    { label: 'Clients', path: PROTECTED_ROUTES.CLIENTS, icon: 'PersonOutline', permSection: 'entreprises', permAction: 'clients' },
    { label: 'Statistiques clients', path: PROTECTED_ROUTES.CLIENT_STATISTICS, icon: 'Assessment', permSection: 'entreprises', permAction: 'clients' },
    { label: 'Fournisseurs', path: PROTECTED_ROUTES.SUPPLIERS, icon: 'LocalShipping', permSection: 'entreprises', permAction: 'fournisseurs' },
    { label: 'Statistiques fournisseurs', path: PROTECTED_ROUTES.SUPPLIER_STATISTICS, icon: 'Assessment', permSection: 'entreprises', permAction: 'fournisseurs' },
  ]},
  { label: 'Paramétrage', path: PROTECTED_ROUTES.SETTINGS, icon: 'Settings', roles: ['admin', 'manager'], permSection: 'parametrage' },
  { label: 'Gestion des utilisateurs', path: PROTECTED_ROUTES.USERS, icon: 'ManageAccounts', roles: ['admin'], permSection: 'utilisateurs' },
  { label: 'Articles et Services', path: PROTECTED_ROUTES.NOMENCLATURE, icon: 'Category', permSection: 'nomenclature', dividerAfter: true },

  // --- Bloc technique ---
  { label: 'Connexions BDD', path: PROTECTED_ROUTES.CONNECTIONS, icon: 'Storage', roles: ['admin'] },
  { label: 'Synchronisation', path: PROTECTED_ROUTES.SYNC, icon: 'Sync', roles: ['admin', 'manager'], dividerAfter: true },

  // --- Bloc aide ---
  { label: 'Aide', path: PROTECTED_ROUTES.HELP, icon: 'HelpOutline' },
];
